import { useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import type { Client } from '@microsoft/microsoft-graph-client';
import { useMsal } from '@azure/msal-react';
import { acquireGraphToken, useAuth } from '../auth/useAuth';
import { createGraphClient, getSiteId } from '../services/graphClient';
import { getWriter } from '../services/commitAdapter';
import type { ActionIO } from '../services/actionService';
import { createOutbox } from '../lib/outbox';
import { ActionRuntimeContext, trackCommitETags } from '../hooks/useActionRuntime';
import type { ActionRuntime } from '../hooks/useActionRuntime';

/** The outbox's clock (module level: it is read when the outbox runs, never during render). */
const clock = (): number => Date.now();

interface SiteState {
  client: Client;
  siteId: string | null;
  error: string | null;
}

/**
 * Provides the writer, the Graph I/O context and the shell's one outbox (see useActionRuntime).
 *
 * Lifecycle (13-06 contract): `replay()` on load and on `visibilitychange` back to visible;
 * `flushAll()` on `visibilitychange` to hidden (a normal fetch, not keepalive: Pitfall 2); the
 * browser's leave-page prompt while `pending() > 0`. Only the site id is resolved here: the
 * Action Register lists are addressed by GUID (AR_LISTS), so no title lookups are needed.
 */
export default function ActionRuntimeProvider({ children }: { children: ReactNode }) {
  const { isAuthenticated, user } = useAuth();
  const { instance } = useMsal();
  const upn = user?.email ?? '';

  const writer = useMemo(() => getWriter(), []);

  // One client per sign-in. The token comes from the stable MSAL instance when it is needed.
  const client = useMemo(
    () => (isAuthenticated ? createGraphClient(() => acquireGraphToken(instance)) : null),
    [isAuthenticated, instance],
  );

  const [site, setSite] = useState<SiteState | null>(null);
  useEffect(() => {
    if (!client) return;
    let cancelled = false;
    getSiteId(client)
      .then((siteId) => {
        if (!cancelled) setSite({ client, siteId, error: null });
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          setSite({ client, siteId: null, error: err instanceof Error ? err.message : 'Could not reach the Q&S site' });
        }
      });
    return () => {
      cancelled = true;
    };
  }, [client]);

  const current = site && site.client === client ? site : null;
  const siteId = current?.siteId ?? null;
  const siteError = current?.error ?? null;

  const io = useMemo<ActionIO | null>(() => (client && siteId ? { client, siteId } : null), [client, siteId]);

  const runtime = useMemo(() => {
    if (!io || !upn) return { outbox: null, eTagFor: () => undefined };
    const { bindings, eTagFor } = trackCommitETags(writer.outboxBindings(io), clock);
    return {
      outbox: createOutbox({ storage: writer.outboxStorage(), now: clock, currentUpn: () => upn, ...bindings }),
      eTagFor,
    };
  }, [io, upn, writer]);
  const outbox = runtime.outbox;
  const eTagFor = runtime.eTagFor as ActionRuntime['eTagFor'];

  useEffect(() => {
    if (!outbox) return;
    const replay = () => {
      outbox.replay().catch((err: unknown) => console.warn('[outbox] replay failed:', err));
    };
    const onVisibility = () => {
      if (document.visibilityState === 'hidden') {
        outbox.flushAll().catch((err: unknown) => console.warn('[outbox] flush on hide failed:', err));
      } else {
        replay();
      }
    };
    const onBeforeUnload = (e: BeforeUnloadEvent) => {
      if (outbox.pending() > 0) {
        e.preventDefault();
        e.returnValue = '';
      }
    };
    replay();
    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => {
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('beforeunload', onBeforeUnload);
    };
  }, [outbox]);

  // 13-25 (D-13-L-5, ISS-263): audit rows a register save still owes are retried once the site resolves, on every
  // load, for the signed-in person only, through the Writer (practice owes nothing). The record page retries its own.
  useEffect(() => {
    if (!io || !upn) return;
    writer.retryRecordAudit(io, { upn }).catch((err: unknown) => console.warn('[record audit] retry on load failed:', err));
  }, [io, upn, writer]);

  const value = useMemo<ActionRuntime>(
    () => ({ mode: writer.mode, writer, io, outbox, siteError, eTagFor }),
    [writer, io, outbox, siteError, eTagFor],
  );

  return <ActionRuntimeContext.Provider value={value}>{children}</ActionRuntimeContext.Provider>;
}
